import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  lighten,
  ListItemIcon,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as LogOutIcon } from "../../assets/menu-logout.svg";
import { ReactComponent as ProfileIcon } from "../../assets/menu-profile.svg";
import { useCustomisation } from "../../context/CustomisationContext";
import { useLanguages } from "../../context/LanguagesContext";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { brand, gray } from "../../theme/themePrimitives";
import { getInitials, getShadeColour } from "../../utils/helpers";
import { EllipsisTooltip } from "../EllipsisTooltip/EllipsisTooltip";
import { handleLogout } from "../../utils/authUtils";
import { getEffectiveRoles, getUserHighestRoleLevel } from "../../utils/roleUtils";
import { RoleLevel } from "../../services/api/types/users/users";
import Logo from "../../authentication/components/Logo";
import { useGuestBootstrap } from "../../hooks/useGuestBootstrap";
import { useSelectPortal } from "../../hooks/useSelectPortal";

interface NavDesktopProps {
  selectError?: string;
  sidebarOpen?: boolean;
  isErrorComponent?: boolean;
  pageBgColor?: string;
}

export const NAVBAR_HEIGHT = "72px";

export const NavDesktop: React.FC<NavDesktopProps> = ({
  selectError,
  sidebarOpen,
  isErrorComponent = false,
  pageBgColor,
}) => {
  const [username, setUsername] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, dispatch: authDispatch } = useAuth();
  const { languages, dispatch: languagesDispatch } = useLanguages();
  const { portals, dispatch: portalsDispatch } = usePortal();
  const { customisations, dispatch: customisationsDispatch } =
    useCustomisation();
  const runGuestBootstrap = useGuestBootstrap();
  const selectPortal = useSelectPortal();
  const { portalUid } = useParams();

  const pagesWithoutPortalSelection = [
    "/portal-settings",
    "/portal-design",
    "/portal-translation",
    "/portal-pages",
    "/new-report",
    "/group-cards",
    "/assignment-uploads",
    "/rules",
    "/email-template/",
    "/add-email-template",
    "/email-send",
    "/manage-assignments",
    "/add-new-user",
    "/edit-attributes",
    "/assignments/add",
    "/edit",
    "/edit-assignment",
    "/delete-assignment",
    "/assignment/",
    "/assignments/",
    "/user-activity-report",
    "/assignment-activity-report",
    "/user-report",
  ];
  const exactPagesWithoutPortalSelection = ["/portal-configuration"];

  const disablePortalsDropdown =
    exactPagesWithoutPortalSelection.includes(location.pathname) ||
    pagesWithoutPortalSelection.some((path) =>
      location.pathname.includes(path),
    );

  useEffect(() => {
    if (auth.user) {
      setUsername(`${auth.user.firstName} ${auth.user.lastName}`);
      // Reset avatar error state when user changes
      setAvatarImageError(false);
    }
  }, [auth.user]);

  const setSelectedLanguage = (e: string) => {
    languagesDispatch({ type: "SET_SELECTED_LANGUAGE", payload: e });
  };

  useEffect(() => {
    if (!disablePortalsDropdown) {
      if (
        languages.portalDesignLanguages.length > 0 &&
        !languages.selectedLanguage
      ) {
        setSelectedLanguage(languages.portalDesignLanguages[0].langugageCode);
      }
    } else {
      if (languages.languages.length > 0 && !languages.selectedLanguage) {
        setSelectedLanguage(languages.languages[0].langugageCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const selectedPortal = portals.portals.find(
      (p) => p.portalUid === e.target.value,
    );
    if (selectedPortal) {
      selectPortal(selectedPortal);
    }

    if (selectedPortal && portalUid) {
      const newPortalUid = selectedPortal.portalUid;
      const newPath = location.pathname.replace(portalUid, newPortalUid);
      navigate(newPath, { replace: true });
    }
  };

  const handleLogoutClick = async () => {
    if (auth.user?.email) {
      await handleLogout({
        userEmail: auth.user.email,
        authDispatch,
        customisationsDispatch,
        portalsDispatch,
        runGuestBootstrap,
        navigate,
        showToast,
        setLoading,
      });
    }
  };

  const accountOptions = [
    {
      Icon: ProfileIcon,
      title: "View Profile",
      onClick: () => navigate("/profile"),
    },
    {
      Icon: LogOutIcon,
      title: "Log Out",
      onClick: () => handleLogoutClick(),
    },
  ];

  const navbarBgColor =
    customisations.customisations.GPC.navbarSettings?.navbarBgColor || undefined;

  const showNavDivider =
    !navbarBgColor ||
    !pageBgColor ||
    navbarBgColor.toLowerCase() !== pageBgColor.toLowerCase();

  const dropdownBg =
    customisations.customisations.GPC.navbarSettings?.navbarDropdownBgColor;
  const dropdownText =
    customisations.customisations.GPC.navbarSettings?.navbarDropdownTextColor;
  const dropdownHoverBg = dropdownBg
    ? getShadeColour(dropdownBg, 15)
    : undefined;
  const lightenedDropdownBg = lighten(dropdownBg || "#ffffff", 0.7);

  const menuProps = {
    disableScrollLock: true,
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
      },
    },
  };

  const isPortalSelectDisabled = disablePortalsDropdown || !!isErrorComponent;

  const userRoles = getEffectiveRoles(auth.user, portals.selectedPortal ?? null);
  const userLevel = getUserHighestRoleLevel(userRoles);
  const showPortalPicker = userLevel !== null && userLevel >= RoleLevel.PORTAL_ADMIN;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: navbarBgColor,
      }}
    >
      <Box
        sx={{
          padding: "0 2rem",
          paddingLeft: sidebarOpen ? "calc(2rem + 280px)" : "calc(2rem + 64px)",
          height: NAVBAR_HEIGHT,
          backgroundColor: navbarBgColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: (theme) =>
            theme.transitions.create("padding-left", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Logo isLoginPageLogo={false} />
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          {portals.selectedPortal && showPortalPicker && (
            <Select
              disabled={isPortalSelectDisabled}
              IconComponent={ExpandMoreIcon}
              value={portals.selectedPortal?.portalUid ?? ""}
              error={Boolean(selectError)}
              onChange={handleSelectChange}
              MenuProps={menuProps}
              size="small"
              sx={{
                width: "300px",
                backgroundColor: dropdownBg,
                "&:hover": {
                  backgroundColor: portals.selectedPortal.isMain
                    ? `var(--brand-600)`
                    : dropdownHoverBg || dropdownBg,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: gray[300],
                  },
                },
                "&.Mui-disabled": {
                  lightenedDropdownBg,
                },
                "&.Mui-disabled:hover": {
                  lightenedDropdownBg,
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: dropdownBg,
                  "&:hover": {
                    backgroundColor: portals.selectedPortal.isMain
                      ? `var(--brand-600)`
                      : dropdownHoverBg || dropdownBg,
                  },
                },
                "& .MuiSelect-icon": {
                  color: `${dropdownText} !important`,
                },
                ...(portals.selectedPortal.isMain && {
                  backgroundColor: `var(--brand-600)`,
                  "&:hover": {
                    backgroundColor: `var(--brand-600)`,
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: `var(--brand-600)`,
                    "&:hover": {
                      backgroundColor: `var(--brand-600)`,
                    },
                  },
                  "&.Mui-disabled": {
                    backgroundColor: `var(--brand-300)`,
                  },
                  "&.Mui-disabled:hover": {
                    backgroundColor: `var(--brand-300)`,
                  },
                  "&.Mui-disabled .MuiTypography-body1": {
                    WebkitTextFillColor: "white",
                  },
                  "& .MuiSelect-icon": {
                    color: "white",
                  },
                }),
              }}
              renderValue={(selected) => {
                const selectedPortal = portals.portals?.find(
                  (p) => p.portalUid === selected,
                );
                return (
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: portals.selectedPortal?.isMain
                        ? "white"
                        : dropdownText || gray[600],
                      "&:hover": {
                        color: portals.selectedPortal?.isMain
                          ? "white"
                          : dropdownText || gray[600],
                      },
                    }}
                  >
                    {selectedPortal?.name || selected}
                  </Typography>
                );
              }}
            >
              {portals.portals?.map((item) => {
                const isSelected =
                  item.portalUid === portals.selectedPortal?.portalUid;
                return (
                  <MenuItem
                    key={item.portalUid}
                    value={item.portalUid}
                    selected={isSelected}
                    sx={{
                      backgroundColor: dropdownBg,
                      ...(item.isMain && {
                        "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible":
                          {
                            backgroundColor: "#F9F5FF !important",
                            "&:hover": {
                              backgroundColor: "#F9F5FF !important",
                              textDecoration: "underline",
                            },
                          },
                        "&:hover": {
                          backgroundColor: "#F9F5FF !important",
                          textDecoration: "underline",
                        },
                      }),
                      "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                        {
                          backgroundColor:
                            dropdownHoverBg || dropdownBg || "#F9FAFB",
                        },
                      "&:hover, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                        {
                          backgroundColor:
                            dropdownHoverBg || dropdownBg || "#F9FAFB",
                          textDecoration: "underline",
                        },
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: isSelected ? 600 : 400,
                        color: dropdownText,
                      }}
                    >
                      {item.name}
                    </Typography>
                    {isSelected && (
                      <CheckIcon sx={{ color: dropdownText || brand[600], fontSize: "20px" }} />
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          )}
          {(() => {
            const availableLanguages =
              !disablePortalsDropdown &&
              languages.portalDesignLanguages.length > 0
                ? languages.portalDesignLanguages
                : languages.languages;

            return (
              <Select
                IconComponent={ExpandMoreIcon}
                value={languages.selectedLanguage ?? ""}
                displayEmpty
                onChange={(e) => setSelectedLanguage(e.target.value)}
                MenuProps={menuProps}
                size="small"
                disabled={isErrorComponent}
                sx={{
                  width: "164px",
                  backgroundColor: dropdownBg,
                  "&:hover": {
                    backgroundColor: dropdownHoverBg || dropdownBg,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: gray[300],
                    },
                  },
                  "&.Mui-disabled": {
                    lightenedDropdownBg,
                  },
                  "&.Mui-disabled:hover": {
                    lightenedDropdownBg,
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dropdownBg,
                    "&:hover": {
                      backgroundColor: dropdownHoverBg || dropdownBg,
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: dropdownText,
                  },
                }}
                renderValue={(selected) => {
                  const selectedLang = availableLanguages.find(
                    (lang) => lang.langugageCode === selected,
                  );
                  if (!selectedLang) return selected;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        height: "100%",
                      }}
                    >
                      <Avatar
                        src={selectedLang.flag}
                        alt={selectedLang.name}
                        sx={{ width: 20, height: 20 }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: dropdownText || gray[600],
                        }}
                      >
                        {selectedLang.name}
                      </Typography>
                    </Box>
                  );
                }}
              >
                {availableLanguages.map((lang, index) => {
                  const isSelected =
                    lang.langugageCode === languages.selectedLanguage;
                  return (
                    <MenuItem
                      key={index}
                      value={lang.langugageCode}
                      selected={isSelected}
                      sx={{
                        "&:hover": {
                          textDecoration: "underline",
                        },
                        backgroundColor: dropdownBg,
                        "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                          {
                            backgroundColor: dropdownHoverBg || dropdownBg,
                          },
                        "&:hover, &.Mui-focusVisible": {
                          backgroundColor: dropdownHoverBg || dropdownBg,
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          src={lang.flag}
                          alt={lang.name}
                          sx={{ width: 20, height: 20 }}
                        />
                      </ListItemIcon>
                      <Typography
                        sx={{
                          flex: 1,
                          fontWeight: isSelected ? 600 : 400,
                          color: dropdownText,
                        }}
                      >
                        {lang.name}
                      </Typography>
                      {isSelected && (
                        <CheckIcon
                          sx={{ color: dropdownText || brand[600], fontSize: "20px" }}
                        />
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          })()}
          {loading ? (
            <Box
              sx={{
                minWidth: "150px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Select
              IconComponent={ExpandMoreIcon}
              value={selectedOption}
              displayEmpty
              onChange={(e) => setSelectedOption(e.target.value)}
              MenuProps={menuProps}
              size="small"
              sx={{
                maxWidth: 220,
                backgroundColor: dropdownBg,
                "&:hover": {
                  backgroundColor: dropdownHoverBg || dropdownBg,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: gray[300],
                  },
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: dropdownBg,
                  "&:hover": {
                    backgroundColor: dropdownHoverBg || dropdownBg,
                  },
                },
                "& .MuiSelect-icon": {
                  color: dropdownText,
                },
              }}
              renderValue={() => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      height: "100%",
                      overflow: "hidden",
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      src={
                        auth.user?.thumbnail && !avatarImageError
                          ? auth.user.thumbnail
                          : undefined
                      }
                      onError={() => {
                        setAvatarImageError(true);
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: gray[200],
                        color: gray[600],
                        fontSize: "0.75rem",
                      }}
                    >
                      {getInitials(auth.user?.firstName ?? "", auth.user?.lastName ?? "")}
                    </Avatar>
                    <EllipsisTooltip title={username}>
                      <Typography
                        sx={{
                          color: dropdownText || gray[600],
                          fontWeight: 600,
                        }}
                      >
                        {username}
                      </Typography>
                    </EllipsisTooltip>
                  </Box>
                );
              }}
            >
              {accountOptions.map((option, index) => (
                <Box key={index}>
                  <MenuItem
                    value={option.title}
                    onClick={() => {
                      option.onClick();
                      setSelectedOption("");
                    }}
                    sx={{
                      backgroundColor: dropdownBg,
                      "&.Mui-selected, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible, &.Mui-selected:hover":
                        {
                          backgroundColor: dropdownHoverBg || dropdownBg,
                        },
                      "&:hover, &.Mui-focusVisible": {
                        backgroundColor: dropdownHoverBg || dropdownBg,
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <option.Icon
                        style={{ color: dropdownText || "currentColor" }}
                      />
                    </ListItemIcon>
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: 400,
                        color: dropdownText,
                      }}
                    >
                      {option.title}
                    </Typography>
                  </MenuItem>
                  {index < accountOptions.length - 1 && <Divider />}
                </Box>
              ))}
            </Select>
          )}
        </Box>
      </Box>
      {showNavDivider && <Divider />}
    </Box>
  );
};
